import React, { useEffect, useRef } from "react";

const COLS = 72;
const ROWS = 44;
const LEMON = "230,200,50";
const PALE = "255,240,180";
const GAIN = 1.4;

const wave = (x: number, z: number, t: number) =>
  Math.sin(x * 0.9 + t * 0.6 - z * 0.5) * 0.22 +
  Math.sin(z * 0.8 - t * 0.45 + x * 0.3) * 0.28 +
  Math.sin((x + z) * 1.7 + t * 0.9) * 0.06;

function drawFrame(ctx: CanvasRenderingContext2D, W: number, H: number, t: number, dpr: number) {
  ctx.clearRect(0, 0, W, H);
  const fov = H * 1.1;
  const cx = W * 0.68;
  const horizon = H * 0.14;
  const camY = 1.25;
  const yaw = Math.sin(t * 0.05) * 0.25;
  const cy = Math.cos(yaw);
  const sy = Math.sin(yaw);
  // Below the lg breakpoint the text spans the full width, so dim the whole field instead
  const stacked = W / dpr < 1024;

  for (let j = ROWS - 1; j >= 0; j--) {
    const z = 1.2 + j * 0.22;
    const depth = 1 - j / ROWS;
    for (let i = 0; i < COLS; i++) {
      const x = (i / (COLS - 1) - 0.5) * 9;
      const y = wave(x, z, t);
      const rx = x * cy - (z - 5) * sy;
      const rz = x * sy + (z - 5) * cy + 5;
      if (rz < 0.4) continue;
      const px = cx + (rx / rz) * fov;
      const py = horizon + ((camY - y) / rz) * fov;
      if (px < -5 || px > W + 5 || py < -5 || py > H + 5) continue;
      const lift = Math.max(0, y + 0.25);
      const alpha = Math.min(
        1,
        Math.pow(depth, 1.4) *
          (0.35 + lift * 1.3) *
          GAIN *
          (stacked ? 0.45 : Math.min(1, Math.max(0, (px - W * 0.32) / (W * 0.25))))
      );
      if (alpha < 0.02) continue;
      const size = Math.max(0.8, ((2.6 * dpr) / rz) * 2.2);
      ctx.fillStyle = `rgba(${lift > 0.38 ? PALE : LEMON},${alpha})`;
      ctx.fillRect(px - size / 2, py - size / 2, size, size);
    }
  }
}

/* Fixed full-page layer: the page root needs `isolate` and a black background.
   Decorative only: paints nothing for assistive tech, pauses off-screen and in hidden tabs,
   and holds a single still frame when the visitor prefers reduced motion. */
const DotWaveBackground: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const motionQuery = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    let reduced = motionQuery?.matches === true;
    let visible = true;
    let raf = 0;
    let t = 8;
    let last = performance.now();

    const paint = () => drawFrame(ctx, canvas.width, canvas.height, t, dpr);
    const resize = () => {
      const { width, height } = canvas.getBoundingClientRect();
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      paint();
    };
    const tick = (now: number) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      if (visible && !reduced && !document.hidden) {
        t += dt;
        paint();
      }
      raf = requestAnimationFrame(tick);
    };
    const onMotionChange = (e: MediaQueryListEvent) => {
      reduced = e.matches;
    };
    // The wave belongs to the first screen: it fades to a faint texture once the page scrolls on
    const onScroll = () => {
      const fade = Math.min(1, window.scrollY / (window.innerHeight * 0.8));
      canvas.style.opacity = String(1 - fade * 0.75);
    };

    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(canvas);
    const intersectionObserver = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
    });
    intersectionObserver.observe(canvas);
    motionQuery?.addEventListener("change", onMotionChange);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      resizeObserver.disconnect();
      intersectionObserver.disconnect();
      motionQuery?.removeEventListener("change", onMotionChange);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      data-testid="dot-wave-background"
      className="pointer-events-none fixed inset-0 -z-10 h-full w-full"
    />
  );
};

export default DotWaveBackground;
